import { IntlShape, defineMessages } from 'react-intl';

const messages = defineMessages({
  alreadyOnPlan: {
    id: 'app.sml.upgrade.error.alreadyOnPlan',
    defaultMessage: "You're already on this plan.",
  },
  portalNotConfigured: {
    id: 'app.sml.upgrade.error.portalNotConfigured',
    defaultMessage: "We can't process plan changes right now. Please contact support.",
  },
  storeAppStore: {
    id: 'app.sml.upgrade.error.storeAppStore',
    defaultMessage: 'Your plan was bought in the App Store. Change it there.',
  },
  storeGooglePlay: {
    id: 'app.sml.upgrade.error.storeGooglePlay',
    defaultMessage: 'Your plan was bought on Google Play. Change it there.',
  },
  storeOther: {
    id: 'app.sml.upgrade.error.storeOther',
    defaultMessage: 'Your plan was bought in an app store. Change it there.',
  },
  packsNeedPlan: {
    id: 'app.sml.upgrade.error.packsNeedPlan',
    defaultMessage: 'Credit packs are for paid plans. Choose a plan to get more video credits.',
  },
  generic: {
    id: 'app.sml.upgrade.error.generic',
    defaultMessage: 'Something went wrong opening checkout. Please try again.',
  },
});

/** The shape `createMusic24UpgradeSession`/`createMusic24CheckoutSession` reject with over tRPC. */
interface TRPCLikeError {
  data?: { code?: unknown; reason?: unknown; store?: unknown } | null;
  message?: unknown;
}

function readErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const { data } = error as TRPCLikeError;
  const code = data?.code;
  return typeof code === 'string' ? code : undefined;
}

/** The store of a plan bought in an app store, when the server refused web checkout for it. */
function readStoreRefusal(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const { data } = error as TRPCLikeError;
  if (data?.reason !== 'M24_STORE_SUBSCRIPTION') {
    return null;
  }
  return typeof data.store === 'string' ? data.store : '';
}

function readReason(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const { data } = error as TRPCLikeError;
  return typeof data?.reason === 'string' ? data.reason : null;
}

function readErrorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null) {
    return '';
  }
  const { message } = error as TRPCLikeError;
  return typeof message === 'string' ? message : '';
}

/** The note for a plan that an app store bills: the person changes the plan in that store. */
export function storePlanMessage(store: string, intl: IntlShape): string {
  if (store === 'appStore') {
    return intl.formatMessage(messages.storeAppStore);
  }
  if (store === 'googlePlay') {
    return intl.formatMessage(messages.storeGooglePlay);
  }
  return intl.formatMessage(messages.storeOther);
}

/**
 * Turns a failed upgrade/checkout-session call into a message safe to show a customer. Never
 * returns the raw error text — it can be an internal Stripe message — only ever one of a small,
 * translated set. Callers should still log the raw error for diagnosis.
 */
export function getUpgradeSessionErrorMessage(error: unknown, intl: IntlShape): string {
  const code = readErrorCode(error);

  // The workspace pays for Music24 in an app store. A web checkout would charge twice, so the
  // server refuses it, and the person changes the plan in that store.
  const store = readStoreRefusal(error);
  if (store !== null) {
    return storePlanMessage(store, intl);
  }

  // A credit pack on Free or on the trial: packs are for paid plans.
  if (readReason(error) === 'M24_PACKS_NEED_PLAN') {
    return intl.formatMessage(messages.packsNeedPlan);
  }

  if (code === 'PRECONDITION_FAILED') {
    return intl.formatMessage(messages.portalNotConfigured);
  }

  // Both "you're already on this plan" and "invalid price id" are BAD_REQUEST — only the former
  // gets its own copy. This matches on our own server-authored message text (never Stripe's), so
  // it stays safe even though it's a substring check.
  if (code === 'BAD_REQUEST' && readErrorMessage(error).toLowerCase().includes('already on this plan')) {
    return intl.formatMessage(messages.alreadyOnPlan);
  }

  return intl.formatMessage(messages.generic);
}
